import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { Plus, Trash2, Save, RotateCcw } from '@/components/ui/lucide-memo';
import { DEFAULT_PROPERTY_SCORING_RUBRIC, ScoringCriterion } from '#types/types';
import { useKernelStore } from '@/kernel/kernelStores';
import clsx from 'clsx';

const translations = {
  en: {
    scoringStrategy: 'Scoring Strategy',
    addCriterion: 'Add criterion',
    edit: 'Edit',
    cancel: 'Cancel',
    criterionLabel: 'Criterion',
    criterionWeight: 'Points',
    criterionMinimumScore: 'Min score',
    noCriteria: 'No criteria defined. Fater will propose a strategy.',
    save: 'Save Changes',
    reset: 'Reset',
    resetToDefault: 'Reset default',
    resetToDefaultConfirm: 'Reset scoring rubric to the default configuration?',
    unsaved: 'Unsaved changes',
  },
  fr: {
    scoringStrategy: 'Strategie de scoring',
    addCriterion: 'Ajouter un critere',
    edit: 'Modifier',
    cancel: 'Annuler',
    criterionLabel: 'Critere',
    criterionWeight: 'Points',
    criterionMinimumScore: 'Note min',
    noCriteria: 'Aucun critere defini. Fater proposera une strategie.',
    save: 'Sauvegarder',
    reset: 'Reinitialiser',
    resetToDefault: 'Reset par defaut',
    resetToDefaultConfirm: 'Reinitialiser la rubrique de scoring avec la configuration par defaut ?',
    unsaved: 'Changements non sauvegardes',
  },
};

const MemoSave = React.memo(Save);
const MemoRotateCcw = React.memo(RotateCcw);

interface ScoringRubricEditorProps {
  value: ScoringCriterion[];
  onChange: (value: ScoringCriterion[]) => void;
  variant?: 'default' | 'compactMission';
  defaultRubric?: ScoringCriterion[];
}

const normalizeWeight = (raw: string | number): number => {
  const parsed = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return 1;
  return Math.max(1, Math.min(100, parsed));
};

const normalizeMinimumScore = (raw: string | number, weight: number): number => {
  const parsed = typeof raw === 'number' ? raw : Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed)) return 0;
  const maxAllowed = Math.max(0, Math.round(weight));
  return Math.max(0, Math.min(maxAllowed, Math.round(parsed)));
};

const DIGITS_ONLY_REGEX = /^\d*$/;
const cloneRubric = (rubric: ScoringCriterion[]): ScoringCriterion[] =>
  rubric.map((criterion) => ({ ...criterion }));

const resolveWeightInputValue = (criterion: ScoringCriterion, draft: string | undefined): string => {
  if (draft !== undefined) return draft;
  if (typeof criterion.weight === 'number' && Number.isFinite(criterion.weight)) {
    return String(criterion.weight);
  }
  return '';
};

const resolveMinimumScoreInputValue = (criterion: ScoringCriterion, draft: string | undefined): string => {
  if (draft !== undefined) return draft;
  if (typeof criterion.minimumScore === 'number' && Number.isFinite(criterion.minimumScore)) {
    return String(criterion.minimumScore);
  }
  return '';
};

const blurOnEnter = (event: React.KeyboardEvent<HTMLInputElement>) => {
  if (event.key === 'Enter') {
    (event.currentTarget as HTMLInputElement).blur();
  }
};

interface RubricRowProps {
  criterion: ScoringCriterion;
  index: number;
  weightDraft: string | undefined;
  minimumScoreDraft: string | undefined;
  labelPlaceholder: string;
  onWeightChange: (criterionId: string, rawValue: string) => void;
  onWeightCommit: (criterionId: string, rawDraft: string | undefined) => void;
  onMinimumScoreChange: (criterionId: string, rawValue: string) => void;
  onMinimumScoreCommit: (criterionId: string, rawDraft: string | undefined) => void;
  onLabelChange: (criterionId: string, updates: Partial<ScoringCriterion>) => void;
  onRemove: (criterionId: string) => void;
}

interface CompactRubricRowProps extends RubricRowProps {
  isEditing: boolean;
}

interface DefaultRubricRowProps extends RubricRowProps {
  rowClassName: string;
  labelClassName: string;
  valueClassName: string;
  inputClassName: string;
}

const CompactRubricRow = React.memo(({
  criterion,
  index,
  isEditing,
  weightDraft,
  minimumScoreDraft,
  labelPlaceholder,
  onWeightChange,
  onWeightCommit,
  onMinimumScoreChange,
  onMinimumScoreCommit,
  onLabelChange,
  onRemove,
}: CompactRubricRowProps) => {
  const criterionId = criterion.id;

  const handleWeightChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onWeightChange(criterionId, event.target.value);
  }, [criterionId, onWeightChange]);

  const handleWeightBlur = useCallback(() => {
    onWeightCommit(criterionId, weightDraft);
  }, [criterionId, onWeightCommit, weightDraft]);

  const handleMinimumScoreChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onMinimumScoreChange(criterionId, event.target.value);
  }, [criterionId, onMinimumScoreChange]);

  const handleMinimumScoreBlur = useCallback(() => {
    onMinimumScoreCommit(criterionId, minimumScoreDraft);
  }, [criterionId, onMinimumScoreCommit, minimumScoreDraft]);

  const handleLabelChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onLabelChange(criterionId, { label: event.target.value });
  }, [criterionId, onLabelChange]);

  const handleRemove = useCallback(() => {
    onRemove(criterionId);
  }, [criterionId, onRemove]);

  return (
    <div className="grid grid-cols-[40px_88px_92px_minmax(140px,1fr)_34px] gap-0 border-b border-white/5 last:border-b-0">
      <div className="px-2 py-1.5 flex items-center">
        <span className="text-sm text-white/55 px-0.5">{index + 1}</span>
      </div>
      <div className="px-2 py-1.5 flex items-center">
        {isEditing ? (
          <input
            type="number"
            min={1}
            max={100}
            value={resolveWeightInputValue(criterion, weightDraft)}
            onChange={handleWeightChange}
            onBlur={handleWeightBlur}
            onKeyDown={blurOnEnter}
            className="w-full h-7 rounded bg-[#1F2226] border border-white/10 px-2 text-sm text-white/90 focus:outline-none"
          />
        ) : (
          <span className="text-sm text-white/85 px-0.5">{criterion.weight || '-'}</span>
        )}
      </div>
      <div className="px-2 py-1.5 flex items-center">
        {isEditing ? (
          <input
            type="number"
            min={0}
            max={Math.max(0, Math.round(criterion.weight))}
            value={resolveMinimumScoreInputValue(criterion, minimumScoreDraft)}
            onChange={handleMinimumScoreChange}
            onBlur={handleMinimumScoreBlur}
            onKeyDown={blurOnEnter}
            className="w-full h-7 rounded bg-[#1F2226] border border-white/10 px-2 text-sm text-white/90 focus:outline-none"
            placeholder="-"
          />
        ) : (
          <span className="text-sm text-white/85 px-0.5">
            {typeof criterion.minimumScore === 'number' && Number.isFinite(criterion.minimumScore)
              ? criterion.minimumScore
              : '-'}
          </span>
        )}
      </div>
      <div className="px-2 py-1.5 flex items-center">
        {isEditing ? (
          <input
            type="text"
            value={criterion.label || ''}
            placeholder={labelPlaceholder}
            onChange={handleLabelChange}
            className="w-full h-7 rounded bg-[#1F2226] border border-white/10 px-2 text-sm text-white/90 placeholder:text-white/20 focus:outline-none"
          />
        ) : (
          <span className="text-sm text-white/85 px-0.5 truncate">{criterion.label || '-'}</span>
        )}
      </div>
      <div className="px-1 py-1.5 flex items-center justify-center">
        {isEditing && (
          <button
            onClick={handleRemove}
            className="p-1 text-white/25 hover:text-rose-400 transition-colors"
            aria-label="Remove criterion"
          >
            <Trash2 size={12} />
          </button>
        )}
      </div>
    </div>
  );
});

CompactRubricRow.displayName = 'CompactRubricRow';

const DefaultRubricRow = React.memo(({
  criterion,
  index,
  weightDraft,
  minimumScoreDraft,
  labelPlaceholder,
  rowClassName,
  labelClassName,
  valueClassName,
  inputClassName,
  onWeightChange,
  onWeightCommit,
  onMinimumScoreChange,
  onMinimumScoreCommit,
  onLabelChange,
  onRemove,
}: DefaultRubricRowProps) => {
  const criterionId = criterion.id;

  const handleWeightChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onWeightChange(criterionId, event.target.value);
  }, [criterionId, onWeightChange]);

  const handleWeightBlur = useCallback(() => {
    onWeightCommit(criterionId, weightDraft);
  }, [criterionId, onWeightCommit, weightDraft]);

  const handleMinimumScoreChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onMinimumScoreChange(criterionId, event.target.value);
  }, [criterionId, onMinimumScoreChange]);

  const handleMinimumScoreBlur = useCallback(() => {
    onMinimumScoreCommit(criterionId, minimumScoreDraft);
  }, [criterionId, onMinimumScoreCommit, minimumScoreDraft]);

  const handleLabelChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    onLabelChange(criterionId, { label: event.target.value });
  }, [criterionId, onLabelChange]);

  const handleRemove = useCallback(() => {
    onRemove(criterionId);
  }, [criterionId, onRemove]);

  return (
    <div className={rowClassName}>
      <div className={labelClassName}>{index + 1}</div>
      <div className={valueClassName}>
        <input
          type="number"
          min={1}
          max={100}
          value={resolveWeightInputValue(criterion, weightDraft)}
          onChange={handleWeightChange}
          onBlur={handleWeightBlur}
          onKeyDown={blurOnEnter}
          className={inputClassName}
          placeholder="0"
        />
      </div>
      <div className={valueClassName}>
        <input
          type="number"
          min={0}
          max={Math.max(0, Math.round(criterion.weight))}
          value={resolveMinimumScoreInputValue(criterion, minimumScoreDraft)}
          onChange={handleMinimumScoreChange}
          onBlur={handleMinimumScoreBlur}
          onKeyDown={blurOnEnter}
          className={inputClassName}
          placeholder="-"
        />
      </div>
      <div className={valueClassName}>
        <input
          type="text"
          value={criterion.label || ''}
          placeholder={labelPlaceholder}
          onChange={handleLabelChange}
          className={clsx(inputClassName, 'text-[#E8E8E8] placeholder:text-[#4D4D4D]')}
        />
      </div>
      <div className="flex items-center justify-center">
        <button
          onClick={handleRemove}
          className="p-1 text-[#4D4D4D] hover:text-rose-400 transition-colors opacity-0 group-hover:opacity-100"
          aria-label="Remove criterion"
        >
          <Trash2 size={12} />
        </button>
      </div>
    </div>
  );
});

DefaultRubricRow.displayName = 'DefaultRubricRow';

export const ScoringRubricEditor = React.memo(({ value, onChange, variant = 'default', defaultRubric }: ScoringRubricEditorProps) => {
  const lang = useKernelStore((state) => state.language);
  const t = translations[lang];
  const isCompact = variant === 'compactMission';
  const effectiveDefaultRubric = defaultRubric ?? DEFAULT_PROPERTY_SCORING_RUBRIC;

  const [localValue, setLocalValue] = useState<ScoringCriterion[]>(value);
  const [weightDrafts, setWeightDrafts] = useState<Record<string, string>>({});
  const [minimumScoreDrafts, setMinimumScoreDrafts] = useState<Record<string, string>>({});
  const [isSaving, setIsSaving] = useState(false);
  const [isCompactEditing, setIsCompactEditing] = useState(false);
  const lastValuePropRef = useRef(JSON.stringify(value));

  const isDirty = useMemo(() => JSON.stringify(localValue) !== JSON.stringify(value), [localValue, value]);

  useEffect(() => {
    if (isCompact && isCompactEditing) return;
    const stringified = JSON.stringify(value);
    if (stringified !== lastValuePropRef.current) {
      setLocalValue(value);
      setWeightDrafts({});
      setMinimumScoreDrafts({});
      lastValuePropRef.current = stringified;
    }
  }, [value, isCompact, isCompactEditing]);

  const updateCriterion = useCallback((id: string, updates: Partial<ScoringCriterion>) => {
    setLocalValue((prev) =>
      prev.map((criterion) => (criterion.id === id ? { ...criterion, ...updates } : criterion))
    );
  }, []);

  const applyDrafts = useCallback((criteria: ScoringCriterion[]): ScoringCriterion[] => {
    if (Object.keys(weightDrafts).length === 0 && Object.keys(minimumScoreDrafts).length === 0) {
      return criteria;
    }

    return criteria.map((criterion) => {
      const rawWeightDraft = weightDrafts[criterion.id];
      const nextWeight = rawWeightDraft === undefined
        ? normalizeWeight(criterion.weight)
        : rawWeightDraft.trim() === ''
          ? 1
          : normalizeWeight(rawWeightDraft);

      const rawMinimumScoreDraft = minimumScoreDrafts[criterion.id];
      const computedMinimumScore = rawMinimumScoreDraft === undefined
        ? (typeof criterion.minimumScore === 'number' && Number.isFinite(criterion.minimumScore)
          ? normalizeMinimumScore(criterion.minimumScore, nextWeight)
          : undefined)
        : rawMinimumScoreDraft.trim() === ''
          ? undefined
          : normalizeMinimumScore(rawMinimumScoreDraft, nextWeight);

      const nextCriterion: ScoringCriterion = {
        ...criterion,
        weight: nextWeight,
      };

      if (typeof computedMinimumScore === 'number') {
        nextCriterion.minimumScore = computedMinimumScore;
      } else {
        delete nextCriterion.minimumScore;
      }

      return nextCriterion;
    });
  }, [minimumScoreDrafts, weightDrafts]);

  const handleWeightInputChange = useCallback((criterionId: string, rawValue: string) => {
    if (!DIGITS_ONLY_REGEX.test(rawValue)) return;
    setWeightDrafts((prev) => ({ ...prev, [criterionId]: rawValue }));
    setLocalValue((prev) =>
      prev.map((criterion) => {
        if (criterion.id !== criterionId) return criterion;
        const nextWeight = rawValue.trim() === '' ? 1 : normalizeWeight(rawValue);
        const nextCriterion: ScoringCriterion = {
          ...criterion,
          weight: nextWeight,
        };
        if (typeof criterion.minimumScore === 'number' && Number.isFinite(criterion.minimumScore)) {
          nextCriterion.minimumScore = normalizeMinimumScore(criterion.minimumScore, nextWeight);
        }
        return nextCriterion;
      }),
    );
  }, []);

  const commitWeightDraft = useCallback((criterionId: string, rawDraft: string | undefined) => {
    if (rawDraft === undefined) return;
    setLocalValue((prev) =>
      prev.map((criterion) => {
        if (criterion.id !== criterionId) return criterion;
        const nextWeight = rawDraft.trim() === '' ? 1 : normalizeWeight(rawDraft);
        const nextCriterion: ScoringCriterion = {
          ...criterion,
          weight: nextWeight,
        };
        if (typeof criterion.minimumScore === 'number' && Number.isFinite(criterion.minimumScore)) {
          nextCriterion.minimumScore = normalizeMinimumScore(criterion.minimumScore, nextWeight);
        }
        return nextCriterion;
      }),
    );
    setWeightDrafts((prev) => {
      if (!(criterionId in prev)) return prev;
      const next = { ...prev };
      delete next[criterionId];
      return next;
    });
  }, []);

  const handleMinimumScoreInputChange = useCallback((criterionId: string, rawValue: string) => {
    if (!DIGITS_ONLY_REGEX.test(rawValue)) return;
    setMinimumScoreDrafts((prev) => ({ ...prev, [criterionId]: rawValue }));
    setLocalValue((prev) =>
      prev.map((criterion) => {
        if (criterion.id !== criterionId) return criterion;
        if (rawValue.trim() === '') {
          const nextCriterion = { ...criterion };
          delete nextCriterion.minimumScore;
          return nextCriterion;
        }
        return {
          ...criterion,
          minimumScore: normalizeMinimumScore(rawValue, criterion.weight),
        };
      }),
    );
  }, []);

  const commitMinimumScoreDraft = useCallback((criterionId: string, rawDraft: string | undefined) => {
    if (rawDraft === undefined) return;
    setLocalValue((prev) =>
      prev.map((criterion) => {
        if (criterion.id !== criterionId) return criterion;
        if (rawDraft.trim() === '') {
          const nextCriterion = { ...criterion };
          delete nextCriterion.minimumScore;
          return nextCriterion;
        }
        return {
          ...criterion,
          minimumScore: normalizeMinimumScore(rawDraft, criterion.weight),
        };
      }),
    );
    setMinimumScoreDrafts((prev) => {
      if (!(criterionId in prev)) return prev;
      const next = { ...prev };
      delete next[criterionId];
      return next;
    });
  }, []);

  const handleSave = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsSaving(true);
    try {
      const normalizedValue = applyDrafts(localValue);
      setLocalValue(normalizedValue);
      setWeightDrafts({});
      setMinimumScoreDrafts({});
      await onChange(normalizedValue);
    } finally {
      setIsSaving(false);
    }
  }, [applyDrafts, localValue, onChange]);

  const handleReset = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setLocalValue(value);
    setWeightDrafts({});
    setMinimumScoreDrafts({});
  }, [value]);

  const handleResetToDefault = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSaving) return;
    const confirmed = typeof window === 'undefined' ? true : window.confirm(t.resetToDefaultConfirm);
    if (!confirmed) return;

    const nextValue = cloneRubric(effectiveDefaultRubric);
    setLocalValue(nextValue);
    setWeightDrafts({});
    setMinimumScoreDrafts({});
    setIsSaving(true);
    try {
      await onChange(nextValue);
      if (isCompact) {
        setIsCompactEditing(false);
      }
    } finally {
      setIsSaving(false);
    }
  }, [effectiveDefaultRubric, isCompact, isSaving, onChange, t.resetToDefaultConfirm]);

  const addCriterion = useCallback(() => {
    const id = `crit_${Math.random().toString(36).substring(2, 9)}`;
    setLocalValue((prev) => [...prev, { id, label: '', description: '', weight: 5, minimumScore: undefined }]);
  }, []);

  const removeCriterion = useCallback((id: string) => {
    setLocalValue((prev) => prev.filter((criterion) => criterion.id !== id));
    setWeightDrafts((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
    setMinimumScoreDrafts((prev) => {
      if (!(id in prev)) return prev;
      const next = { ...prev };
      delete next[id];
      return next;
    });
  }, []);

  const startCompactEdit = useCallback(() => {
    setLocalValue(value);
    setWeightDrafts({});
    setMinimumScoreDrafts({});
    setIsCompactEditing(true);
  }, [value]);

  const cancelCompactEdit = useCallback(() => {
    setLocalValue(value);
    setWeightDrafts({});
    setMinimumScoreDrafts({});
    setIsCompactEditing(false);
  }, [value]);

  const saveCompactEdit = useCallback(async () => {
    if (!isDirty) {
      setIsCompactEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      const normalizedValue = applyDrafts(localValue);
      setLocalValue(normalizedValue);
      setWeightDrafts({});
      setMinimumScoreDrafts({});
      await onChange(normalizedValue);
      setIsCompactEditing(false);
    } finally {
      setIsSaving(false);
    }
  }, [applyDrafts, isDirty, localValue, onChange]);

  if (isCompact) {
    return (
      <div className="border-t border-white/5 pt-4 space-y-3">
        <div className="flex items-center justify-between px-1">
          <span className="text-sm font-medium text-white/80">{t.scoringStrategy}</span>
          <div className="flex items-center gap-1.5">
            <button
              onClick={handleResetToDefault}
              disabled={isSaving}
              className={clsx(
                'rounded bg-white/5 border border-white/10 px-2 py-1 text-[11px] transition-colors',
                isSaving
                  ? 'text-white/30 cursor-not-allowed'
                  : 'text-white/70 hover:text-white hover:bg-white/10',
              )}
            >
              {t.resetToDefault}
            </button>
            {isCompactEditing ? (
              <>
                <button
                  onClick={cancelCompactEdit}
                  className="rounded bg-white/5 border border-white/10 px-2 py-1 text-[11px] text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                >
                  {t.cancel}
                </button>
                <button
                  onClick={saveCompactEdit}
                  disabled={isSaving}
                  title={t.save}
                  aria-label={t.save}
                  className={clsx(
                    "flex items-center justify-center h-7 w-7 rounded border transition-colors",
                    isSaving
                      ? "bg-white/5 border-white/10 text-white/30 cursor-not-allowed"
                      : "bg-white/5 border-white/10 text-white/60 hover:text-white hover:bg-white/10"
                  )}
                >
                  {isSaving ? <Plus size={12} className="animate-spin" /> : <Save size={12} />}
                </button>
                <button
                  onClick={addCriterion}
                  className="flex items-center gap-1 rounded bg-white/5 border border-white/10 px-2 py-1 text-[11px] text-white/70 hover:text-white hover:bg-white/10 transition-colors"
                >
                  <Plus size={12} />
                  {t.addCriterion}
                </button>
              </>
            ) : (
              <button
                onClick={startCompactEdit}
                className="rounded bg-white/5 border border-white/10 px-2 py-1 text-[11px] text-white/80 hover:text-white hover:bg-white/10 transition-colors"
              >
                {t.edit}
              </button>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-white/10 overflow-hidden bg-white/[0.02]">
          <div className="grid grid-cols-[40px_88px_92px_minmax(140px,1fr)_34px] gap-0 border-b border-white/10 bg-white/[0.03]">
            <div className="px-2 py-2 text-[11px] text-white/55 font-medium">#</div>
            <div className="px-2 py-2 text-[11px] text-white/55 font-medium">{t.criterionWeight}</div>
            <div className="px-2 py-2 text-[11px] text-white/55 font-medium">{t.criterionMinimumScore}</div>
            <div className="px-2 py-2 text-[11px] text-white/55 font-medium">{t.criterionLabel}</div>
            <div />
          </div>

          {localValue.length === 0 ? (
            <div className="px-3 py-4 text-center text-xs text-white/30 italic">{t.noCriteria}</div>
          ) : (
            <div>
              {localValue.map((criterion, index) => (
                <CompactRubricRow
                  key={criterion.id}
                  criterion={criterion}
                  index={index}
                  isEditing={isCompactEditing}
                  weightDraft={weightDrafts[criterion.id]}
                  minimumScoreDraft={minimumScoreDrafts[criterion.id]}
                  labelPlaceholder={t.criterionLabel}
                  onWeightChange={handleWeightInputChange}
                  onWeightCommit={commitWeightDraft}
                  onMinimumScoreChange={handleMinimumScoreInputChange}
                  onMinimumScoreCommit={commitMinimumScoreDraft}
                  onLabelChange={updateCriterion}
                  onRemove={removeCriterion}
                />
              ))}
            </div>
          )}
        </div>

        {isCompactEditing && isDirty && <div className="px-1 text-[10px] text-white/35">{t.unsaved}</div>}
      </div>
    );
  }

  const GRID_CLS = 'grid grid-cols-[40px_88px_92px_minmax(140px,1fr)_34px] gap-0 items-center';
  const ROW_CLS = 'h-10 px-4 border-b border-[#242424] text-[13px] transition-colors hover:bg-[#171717]';
  const LABEL_CLS = 'px-2 text-[#6E6E6E] font-normal truncate';
  const VALUE_CLS = 'px-2 text-[#E8E8E8] font-normal';
  const INPUT_CLS = 'bg-transparent border-none text-[13px] text-[#E8E8E8] focus:outline-none w-full px-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none';

  return (
    <div className="flex flex-col h-full text-white font-sans overflow-hidden">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-[#242424]">
        <div className="flex items-center gap-2">
          <span className={clsx('text-[11px] font-normal transition-colors', isDirty ? 'text-emerald-400' : 'text-[#B5B5B5]')}>
            {t.scoringStrategy}
          </span>
          {isDirty && <span className="text-[10px] text-emerald-500/60 animate-pulse">{t.unsaved}</span>}
        </div>

        <div className="flex items-center gap-1.5">
          {isDirty && (
            <>
              <button
                onClick={handleReset}
                className="flex items-center gap-1 px-2 py-1 text-[11px] text-[#6E6E6E] hover:text-[#B5B5B5] transition-colors"
                disabled={isSaving}
              >
                <MemoRotateCcw size={12} />
                {t.reset}
              </button>
              <button
                onClick={handleSave}
                className="flex items-center gap-1 px-2 py-1 text-[11px] text-emerald-400 hover:text-emerald-300 transition-colors"
                disabled={isSaving}
              >
                {isSaving ? <Plus className="animate-spin" size={12} /> : <MemoSave size={12} />}
                {t.save}
              </button>
            </>
          )}
          <button
            onClick={handleResetToDefault}
            disabled={isSaving}
            className={clsx(
              'flex items-center gap-1 px-2 py-1 text-[11px] transition-colors',
              isSaving ? 'text-[#4D4D4D] cursor-not-allowed' : 'text-[#6E6E6E] hover:text-[#B5B5B5]',
            )}
          >
            <MemoRotateCcw size={12} />
            {t.resetToDefault}
          </button>
          <button
            onClick={addCriterion}
            className="flex items-center gap-1 px-2 py-1 text-[11px] text-[#6E6E6E] hover:text-[#B5B5B5] transition-colors"
          >
            <Plus size={12} />
            {t.addCriterion}
          </button>
        </div>
      </div>

      {/* Column header */}
      <div className={clsx(GRID_CLS, 'h-9 border-b border-[#242424] px-4 text-[11px] font-normal text-[#6E6E6E]')}>
        <div className="px-2">#</div>
        <div className="px-2">{t.criterionWeight}</div>
        <div className="px-2">{t.criterionMinimumScore}</div>
        <div className="px-2">{t.criterionLabel}</div>
        <div />
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {localValue.length === 0 ? (
          <div className="px-4 py-4 text-center text-xs text-[#4D4D4D] italic">{t.noCriteria}</div>
        ) : (
          localValue.map((criterion, index) => (
            <DefaultRubricRow
              key={criterion.id}
              criterion={criterion}
              index={index}
              weightDraft={weightDrafts[criterion.id]}
              minimumScoreDraft={minimumScoreDrafts[criterion.id]}
              labelPlaceholder={t.criterionLabel}
              rowClassName={clsx(GRID_CLS, ROW_CLS, 'group')}
              labelClassName={LABEL_CLS}
              valueClassName={VALUE_CLS}
              inputClassName={INPUT_CLS}
              onWeightChange={handleWeightInputChange}
              onWeightCommit={commitWeightDraft}
              onMinimumScoreChange={handleMinimumScoreInputChange}
              onMinimumScoreCommit={commitMinimumScoreDraft}
              onLabelChange={updateCriterion}
              onRemove={removeCriterion}
            />
          ))
        )}
      </div>
    </div>
  );
});

ScoringRubricEditor.displayName = 'ScoringRubricEditor';
